import { CheckboxWithToolTip } from 'components/WithTooltip';
import type { BimWorkOrderOtherData, ConstructionTypeData } from 'gql/graphql';
import Styles from 'pages/Project/UerProject/WorkOrder/AddOrderOther/AddOrderOther.module.css';
import type { TableColumn } from 'uikit';
import { Checkbox, cnMixSpace } from 'uikit';

import cn from 'classnames';
import { noun } from 'plural-ru';
import { useCallback, useMemo, useState } from 'react';

type TRowWithCollapseOpt = BimWorkOrderOtherData & {
    id: string;
    nestLevel?: number;
    parentId?: number;
    objectId?: number;
};

type TDataWithTypes<T> =
    | (T & {
          id: any;
          rows: (Partial<ConstructionTypeData> & { id: string; objectId: any; parentId: any })[] | undefined;
      })[]
    | undefined;

interface IUseAddOtherToBimWorkOrderMenu<T> {
    othersWithConstructionTypes: TDataWithTypes<T>;
    disableCheckboxTooltip?: boolean;
}

const defaultCheckedIds = {};

export const useAddOtherToBimWorkOrderMenu = <TData,>(props: IUseAddOtherToBimWorkOrderMenu<TData>) => {
    const { othersWithConstructionTypes, disableCheckboxTooltip } = props;

    const [checkedIds, setCheckedIds] = useState<{
        [key: number | string]: {
            parent: string | boolean;
            value?: string;
            [key: string]: boolean | string | undefined;
        };
    }>(defaultCheckedIds);

    const handleChangeCheckbox = useCallback(
        (row: TRowWithCollapseOpt) =>
            ({ checked }: { checked: boolean }) => {
                if (!row.parentId) {
                    const checkedChildren = othersWithConstructionTypes
                        ?.find((item) => item?.id === row.id)
                        ?.rows?.reduce((acc: Record<number, boolean>, child) => {
                            acc[child.objectId] = checked;

                            return acc;
                        }, {});

                    setCheckedIds((ids) => ({
                        ...ids,
                        [row.id]: {
                            parent: checked,
                            ...checkedChildren,
                        },
                    }));
                } else {
                    setCheckedIds((ids) => {
                        const childrenCounter = row.parentId && Object.entries(ids[row.parentId] || {}).length - 1;
                        const prevCheckedCount =
                            (row.parentId &&
                                Object.entries(ids[row.parentId] || {})?.filter(
                                    ([id, idValue]) => id !== 'parent' && idValue,
                                ).length) ??
                            0;
                        const checkedCount = checked ? prevCheckedCount + 1 : prevCheckedCount - 1;
                        const countsEqual = checkedCount === childrenCounter;
                        const parentState = countsEqual || checkedCount === 0 ? checked : 'intermediate';

                        return {
                            ...ids,
                            [row.parentId ?? '']: {
                                ...ids?.[row.parentId ?? ''],
                                parent: parentState,
                                [row.objectId ?? '']: checked,
                            },
                        };
                    });
                }
            },
        [othersWithConstructionTypes],
    );

    const menuColumn: TableColumn<TRowWithCollapseOpt> = useMemo(
        () => ({
            title: '',
            accessor: 'id',
            width: 40,
            withoutPadding: true,
            renderCell: (row) => {
                const className = `menu-checkbox-${row.id}`;
                const tooltipCounterObj =
                    checkedIds?.[row.id] &&
                    Object.entries(checkedIds[row.id]).reduce(
                        (acc, [id, value]) => {
                            if (id !== 'parent') {
                                acc.totalCount += 1;
                                if (value) {
                                    acc.checkedCount += 1;
                                }
                            }

                            return acc;
                        },
                        { checkedCount: 0, totalCount: 0 },
                    );

                return (
                    <div style={{ margin: '16px 12px' }}>
                        {!disableCheckboxTooltip && row.nestLevel === 0 ? (
                            <CheckboxWithToolTip
                                checkboxProps={{
                                    checked: !!checkedIds?.[row.id]?.parent,
                                    onChange: handleChangeCheckbox(row),
                                    intermediate: checkedIds?.[row.id]?.parent === 'intermediate',
                                    className,
                                }}
                                tooltipProps={{
                                    content: `${tooltipCounterObj?.checkedCount}/${tooltipCounterObj?.totalCount}
                                        ${noun(tooltipCounterObj?.totalCount, ...['объекта', 'объектов'])}`,
                                    className: Styles.tooltip,
                                }}
                            />
                        ) : (
                            <Checkbox
                                checked={Boolean(
                                    row.nestLevel === 0
                                        ? !!checkedIds?.[row.id]?.parent
                                        : row?.parentId &&
                                              row?.objectId &&
                                              checkedIds?.[row?.parentId]?.[row?.objectId],
                                )}
                                onChange={handleChangeCheckbox(row)}
                                className={cn(cnMixSpace({ mT: '3xs' }), className)}
                            />
                        )}
                    </div>
                );
            },
        }),
        [checkedIds, disableCheckboxTooltip, handleChangeCheckbox],
    );

    const setValueForCheckedRow = useCallback(
        (id: number | string) =>
            ({ value }: { value: string }) => {
                setCheckedIds((ids) => ({
                    ...ids,
                    [id]: {
                        ...ids?.[id],
                        value,
                    },
                }));
            },
        [],
    );

    return {
        menuColumn,
        checkedIds,
        setCheckedIds,
        setValueForCheckedRow,
        handleChangeCheckbox,
    };
};
